import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { schedulesService, groundsService } from '../services/supabaseService';
import SharedNavigation from './SharedNavigation';
import '../styles/CreateSchedule.css';

const CreateSchedule = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    numberOfTeams: '',
    gamesPerTeam: '',
    weeksToComplete: ''
  });

  const [groundData, setGroundData] = useState({
    enableGroundAssignment: false,
    selectedGrounds: []
  });

  const [availableGrounds, setAvailableGrounds] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Add success message state
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch available grounds on component mount
  useEffect(() => {
    const fetchGrounds = async () => {
      try {
        const data = await groundsService.getAll();
        setAvailableGrounds(data);
      } catch (error) {
        console.error('Error fetching grounds:', error);
        setErrors(prev => ({ ...prev, grounds: 'Failed to load grounds' }));
      }
    };

    fetchGrounds();
  }, []);

  // Auto-hide success message and navigate after delay
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
        navigate('/ViewSchedule');
      }, 3000); // Navigate after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [showSuccess, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleGroundAssignmentToggle = (e) => {
    const enabled = e.target.checked;
    setGroundData(prev => ({
      ...prev,
      enableGroundAssignment: enabled,
      selectedGrounds: enabled ? prev.selectedGrounds : []
    }));

    if (errors.grounds) {
      setErrors(prev => ({ ...prev, grounds: '' }));
    }
  };

  const handleGroundSelection = (e) => {
    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
    setGroundData(prev => ({
      ...prev,
      selectedGrounds: selectedIds
    }));

    if (errors.grounds) {
      setErrors(prev => ({ ...prev, grounds: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.numberOfTeams || formData.numberOfTeams < 2) {
      newErrors.numberOfTeams = 'At least 2 teams are required';
    }
    if (!formData.gamesPerTeam || formData.gamesPerTeam < 1) {
      newErrors.gamesPerTeam = 'At least 1 game per team is required';
    }
    if (!formData.weeksToComplete || formData.weeksToComplete < 1) {
      newErrors.weeksToComplete = 'At least 1 week is required';
    }
    if (groundData.enableGroundAssignment && groundData.selectedGrounds.length === 0) {
      newErrors.grounds = 'Please select at least one ground when ground assignment is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const scheduleData = {
        numberOfTeams: parseInt(formData.numberOfTeams),
        gamesPerTeam: parseInt(formData.gamesPerTeam),
        weeksToComplete: parseInt(formData.weeksToComplete),
        assigned_grounds: groundData.enableGroundAssignment ? groundData.selectedGrounds : []
      };

      await schedulesService.create(scheduleData);

      const baseMessage = 'Schedule created successfully using round-robin algorithm!';
      const groundMessage = groundData.enableGroundAssignment 
        ? ' Ground assignments distributed evenly across games.'
        : '';
      const futureMessage = ' Other scheduling algorithms coming soon.';
      
      // Replace alert with success message state
      setSuccessMessage(baseMessage + groundMessage + futureMessage);
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Error creating schedule:', error);
      setErrors({ submit: 'Failed to create schedule. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedGroundNames = () => {
    return availableGrounds
      .filter(ground => groundData.selectedGrounds.includes(ground.id))
      .map(ground => ground.ground_name)
      .join(', ');
  };

  const calculateGroundDistribution = () => {
    if (!groundData.enableGroundAssignment || groundData.selectedGrounds.length === 0) {
      return null;
    }

    const totalGames = Math.ceil((parseInt(formData.numberOfTeams) * parseInt(formData.gamesPerTeam)) / 2);
    const numGrounds = groundData.selectedGrounds.length;
    const gamesPerGround = Math.floor(totalGames / numGrounds);
    const remainder = totalGames % numGrounds;

    return {
      totalGames,
      numGrounds,
      gamesPerGround,
      remainder
    };
  };

  return (
    <div className="create-schedule-page">
      <SharedNavigation />

      {/* Success Message Overlay */}
      {showSuccess && (
        <div className="success-overlay">
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h3>Success!</h3>
            <p>{successMessage}</p>
            <div className="success-footer">
              <small>Redirecting to View Schedule in 3 seconds...</small>
            </div>
          </div>
        </div>
      )}

      <div className="create-schedule-content">
        <div className="page-header">
          <h1>Create New Schedule</h1>
          <p>Generate an automated schedule with optional ground assignments using round-robin algorithm</p>
        </div>

        <div className="create-schedule-card">
          <form onSubmit={handleSubmit} className="create-schedule-form">
            {/* Basic Schedule Info */}
            <div className="form-section">
              <h3>Schedule Details</h3>
              
              <div className="form-group">
                <label htmlFor="numberOfTeams">Number of Teams *</label>
                <input
                  type="number"
                  id="numberOfTeams"
                  name="numberOfTeams"
                  value={formData.numberOfTeams}
                  onChange={handleInputChange}
                  className={errors.numberOfTeams ? 'error' : ''}
                  min="2"
                  placeholder="Enter number of teams (minimum 2)"
                  disabled={isSubmitting}
                />
                {errors.numberOfTeams && <span className="error-message">{errors.numberOfTeams}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="gamesPerTeam">Games per Team *</label>
                <input
                  type="number"
                  id="gamesPerTeam"
                  name="gamesPerTeam"
                  value={formData.gamesPerTeam}
                  onChange={handleInputChange}
                  className={errors.gamesPerTeam ? 'error' : ''}
                  min="1"
                  placeholder="Enter games per team (minimum 1)"
                  disabled={isSubmitting}
                />
                {errors.gamesPerTeam && <span className="error-message">{errors.gamesPerTeam}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="weeksToComplete">Weeks/Rounds to Complete *</label>
                <input
                  type="number"
                  id="weeksToComplete"
                  name="weeksToComplete"
                  value={formData.weeksToComplete}
                  onChange={handleInputChange}
                  className={errors.weeksToComplete ? 'error' : ''}
                  min="1"
                  placeholder="Enter number of weeks (minimum 1)"
                  disabled={isSubmitting}
                />
                {errors.weeksToComplete && <span className="error-message">{errors.weeksToComplete}</span>}
              </div>
            </div>

            {/* Ground Assignment Section */}
            <div className="form-section">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={groundData.enableGroundAssignment}
                    onChange={handleGroundAssignmentToggle}
                    disabled={isSubmitting}
                  />
                  <span>Enable Ground Assignment</span>
                </label>
                <small className="form-help">
                  Check this to assign playing grounds to games in your schedule
                </small>
              </div>

              {groundData.enableGroundAssignment && (
                <>
                  <h3>Ground Assignment</h3>
                  
                  <div className="form-group">
                    <label htmlFor="groundSelection">Select Grounds *</label>
                    <select
                      id="groundSelection"
                      multiple
                      value={groundData.selectedGrounds}
                      onChange={handleGroundSelection}
                      className={errors.grounds ? 'error' : ''}
                      disabled={isSubmitting}
                      size="4"
                    >
                      {availableGrounds.map(ground => (
                        <option key={ground.id} value={ground.id}>
                          {ground.ground_name} - {ground.city}
                        </option>
                      ))}
                    </select>
                    <small className="form-help">
                      Hold Ctrl/Cmd to select multiple grounds. Selected: {getSelectedGroundNames() || 'None'}
                      <br />
                      Games will be distributed evenly across selected grounds using round-robin rotation.
                    </small>
                    {errors.grounds && <span className="error-message">{errors.grounds}</span>}
                  </div>

                  {/* Ground Distribution Preview */}
                  {(() => {
                    const distribution = calculateGroundDistribution();
                    return distribution && (
                      <div className="ground-distribution-preview">
                        <h4>Ground Distribution Preview (Round-Robin)</h4>
                        <p>
                          {distribution.gamesPerGround} games per ground
                          {distribution.remainder > 0 && (
                            <span> ({distribution.remainder} ground{distribution.remainder > 1 ? 's' : ''} will get 1 extra game)</span>
                          )}
                        </p>
                        <small>Grounds will be assigned using round-robin rotation for even distribution.</small>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Schedule Preview */}
            <div className="schedule-preview">
              {formData.numberOfTeams && formData.gamesPerTeam && formData.weeksToComplete && (
                <div className="preview-info">
                  <h3>Schedule Preview</h3>
                  <div className="preview-stats">
                    <div className="stat">
                      <span className="stat-label">Algorithm:</span>
                      <span className="stat-value">Round-Robin</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Total Games:</span>
                      <span className="stat-value">
                        {Math.ceil((parseInt(formData.numberOfTeams) * parseInt(formData.gamesPerTeam)) / 2)}
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Games per Week:</span>
                      <span className="stat-value">
                        {Math.ceil((parseInt(formData.numberOfTeams) * parseInt(formData.gamesPerTeam)) / 2 / parseInt(formData.weeksToComplete))}
                      </span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Ground Assignment:</span>
                      <span className="stat-value">
                        {groundData.enableGroundAssignment ? `${groundData.selectedGrounds.length} grounds selected` : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  <small className="algorithm-note">
                    Using round-robin algorithm for fair team matchups and ground distribution. Other scheduling types coming soon!
                  </small>
                </div>
              )}
            </div>

            {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => navigate('/ViewSchedule')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="create-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Generating Schedule...' : 'Generate Schedule'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSchedule;